import { cva } from 'class-variance-authority';
import { Link } from '@tanstack/react-router';
import type { IconType } from 'react-icons';

export interface TabItem {
  label: string;
  current?: boolean;
  onClick?: () => void;
  to?: string;
  icon?: IconType;
}

export interface TabsProps {
  items: TabItem[];
  className?: string;
}

const tabs = cva('border-b border-gray-200');

const tabList = cva('m-0 flex h-[44px] list-none');

const tabItem = cva(
  'flex h-[44px] flex-1 list-none items-center justify-center rounded-t-sm border-b border-solid px-lg',
  {
    variants: {
      isActive: {
        true: 'border-b-2 border-b-primary',
        false: 'border-b border-b-border hover:border-b-2 hover:border-b-primary',
      },
    },
  }
);

const tabButton = cva(
  'flex size-full items-center justify-center gap-xxs text-center text-sm no-underline transition-colors',
  {
    variants: {
      isActive: {
        true: 'font-bold text-primary',
        false: 'font-normal text-main hover:font-bold hover:text-primary',
      },
    },
  }
);

export default function Tabs({ items, className }: TabsProps) {
  return (
    <div className={tabs({ className })}>
      <ul className={tabList()}>
        {items.map((item) => {
          const Icon = item.icon;
          const content = (
            <>
              {Icon ? <Icon size={16} /> : null}
              {item.label}
            </>
          );

          return (
            <li key={item.label} className={tabItem({ isActive: item.current })}>
              {item.to ? (
                <Link to={item.to} className={tabButton({ isActive: item.current })}>
                  {content}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={item.onClick}
                  className={tabButton({ isActive: item.current })}
                >
                  {content}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
